import { useCallback, useEffect, useRef, useState } from 'react';
import type { UnknownAction } from 'redux';
import { ActionCreator } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';

import useReduxStoreContext from '@rover/react-lib/src/context/useReduxStoreContext';
import {
  ActionTypes,
  EffectTypes,
  ExtractActionPayloadType,
  ExtractEffectPayloadType,
  ExtractEffectReturnType,
} from '@rover/react-lib/src/lib/redux-duck';
import isShallowEqual from '@rover/react-lib/src/utils/isShallowEqual';

type Selector = (state: Readonly<unknown>) => unknown;

type ReturnedSelectors<S extends Selector | undefined> = NonNullable<
  S extends Selector ? ReturnType<S> : {} // eslint-disable-line @typescript-eslint/ban-types
>;

type ReturnedEffects<E extends EffectTypes | undefined> = NonNullable<
  E extends EffectTypes ? EffectProps<E> : {} // eslint-disable-line @typescript-eslint/ban-types
>;

type ReturnedActions<A extends ActionTypes | undefined> = NonNullable<
  A extends ActionTypes ? ActionProps<A> : {} // eslint-disable-line @typescript-eslint/ban-types
>;

type ActionProps<T> = {
  [Property in keyof T]: (payload: ExtractActionPayloadType<T[Property]>) => void;
};

type EffectProps<T> = {
  [Property in keyof T]: (
    payload: ExtractEffectPayloadType<T[Property]>
  ) => ExtractEffectReturnType<T[Property]>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppDispatch = ThunkDispatch<any, any, UnknownAction>;

const getDispatchMap = <T extends ActionTypes | EffectTypes>(
  actions: T,
  dispatch: AppDispatch,
  { prevActions, prevDispatchedActions }
): ActionProps<T> => {
  return Object.fromEntries(
    Object.entries(actions).map(([key, action]) => {
      if (!action || typeof action !== 'function') {
        throw new Error(`Connect action values must be functions (action ${key})`);
      }

      return [
        key,
        action === prevActions[key]
          ? prevDispatchedActions[key] // Don't create new function if action hasn't changed
          : (...args) => dispatch(action(...args)),
      ];
    })
  ) as ActionProps<T>;
};

function useDucks<S extends Selector, E extends EffectTypes, A extends ActionTypes>({
  selector,
  effects,
  actions,
  onMount,
  onUnmount,
}: {
  selector?: S;
  effects?: E;
  actions?: A;
  onMount?: ActionCreator<any> | Array<ActionCreator<any>>;
  onUnmount?: ActionCreator<any> | Array<ActionCreator<any>>;
}): {
  dispatch: AppDispatch;
  data: ReturnedSelectors<S> & ReturnedEffects<E> & ReturnedActions<A>;
} {
  type RS = ReturnedSelectors<S>;
  type RE = ReturnedEffects<E>;
  type RA = ReturnedActions<A>;

  const { reduxStore: store } = useReduxStoreContext();
  const { dispatch, getState, subscribe } = store;

  const [prevEffects, setPrevEffects] = useState<E>({} as E);
  const [prevDispatchedEffects, setPrevDispatchedEffects] = useState<RE>();
  const [effectProps, setEffectProps] = useState<RE>({} as RE);
  const [prevActions, setPrevActions] = useState<A>({} as A);
  const [prevDispatchedActions, setPrevDispatchedActions] = useState<RA>();
  const [actionProps, setActionProps] = useState<RA>({} as RA);
  const [selectorProps, setSelectorProps] = useState<RS>(
    (selector ? selector(getState() || {}) : {}) as RS
  );
  const [hasInitialized, setHasInitialized] = useState(false);

  // isMounted must be a ref because we need to not re-render the component when it is changed
  const isMounted = useRef(true);
  // We must have this useEffect, otherwise the dev mode double mount from react StrictMode will break things
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getActions = useCallback((): RA => {
    if (!actions) return {} as RA;
    const dispatchedActions = getDispatchMap<NonNullable<A>>(actions as NonNullable<A>, dispatch, {
      prevActions,
      prevDispatchedActions,
    }) as RA;

    setPrevActions(actions);
    setPrevDispatchedActions(dispatchedActions);
    return dispatchedActions;
  }, [actions, prevActions, prevDispatchedActions]);

  const getEffects = useCallback((): RE => {
    if (!effects) return {} as RE;
    const dispatchedEffects = getDispatchMap<NonNullable<E>>(effects as NonNullable<E>, dispatch, {
      prevActions: prevEffects,
      prevDispatchedActions: prevDispatchedEffects,
    }) as RE;

    setPrevEffects(effects);
    setPrevDispatchedEffects(dispatchedEffects);
    return dispatchedEffects;
  }, [effects, prevEffects, prevDispatchedActions]);

  const updateState = useCallback(
    (compareToNext = true): void => {
      if (!isMounted.current) return; // In-progress actions may cause updateState to be called after the component is unmounted.
      const nextSelectorProps = (selector ? selector(getState() || {}) : {}) as RS;

      if (!compareToNext || !isShallowEqual(nextSelectorProps, selectorProps)) {
        setSelectorProps(nextSelectorProps);
        setEffectProps(getEffects());
        setActionProps(getActions());
      }
    },
    [selectorProps]
  );

  useEffect(() => {
    const unsubscribe = subscribe(updateState);
    return () => unsubscribe();
  }, [updateState]);

  useEffect(() => {
    if (onMount) {
      const onMountActions = Array.isArray(onMount) ? onMount : [onMount];
      onMountActions.forEach((a) => dispatch(a()));
    }

    return () => {
      if (onUnmount) {
        const onUnmountActions = Array.isArray(onUnmount) ? onUnmount : [onUnmount];
        onUnmountActions.forEach((a) => dispatch(a()));
      }
    };
  }, []);

  const init = useCallback(() => {
    updateState(false);
    setEffectProps(getEffects());
    setActionProps(getActions());
  }, []);

  if (!hasInitialized) {
    init();
    setHasInitialized(true);
  }

  return { dispatch, data: { ...selectorProps, ...actionProps, ...effectProps } };
}

export default useDucks;
